/**
 * Bottom-nav glyphs for the family (Cozy Pastel) shell — inline single-stroke SVGs sized 22px and
 * inheriting `currentColor` so the active/idle token colors apply (mirrors the admin shell's
 * `nav-icons`). Purely decorative (the nav label is the accessible name), so each is `aria-hidden`.
 */
const base = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

export function HomeIcon(): React.ReactElement {
  return (
    <svg {...base}>
      <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V9.5Z" />
    </svg>
  );
}

export function BellIcon(): React.ReactElement {
  // A calm bell — the informational notification affordance (distinct from the actionable Inbox tab).
  return (
    <svg {...base}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

export function RewardsIcon(): React.ReactElement {
  return (
    <svg {...base}>
      <path d="M20 12v9H4v-9" />
      <rect x="2" y="7" width="20" height="5" rx="1" />
      <path d="M12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
  );
}

export function ProfileIcon(): React.ReactElement {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <path d="M9 9h.01M15 9h.01" />
    </svg>
  );
}

export function HeroIcon(): React.ReactElement {
  // A friendly sparkle-star — the kid's chosen, growing hero (ADR-0095). Decorative; the label names it.
  return (
    <svg {...base}>
      <path d="M12 3l1.9 4.3L18.5 9l-4.6 1.7L12 15l-1.9-4.3L5.5 9l4.6-1.7L12 3Z" />
      <path d="M18 16l.8 1.8L20.5 18.5l-1.7.7L18 21l-.8-1.8L15.5 18.5l1.7-.7L18 16Z" />
    </svg>
  );
}

export function CalendarIcon(): React.ReactElement {
  return (
    <svg {...base}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

export function FamilyIcon(): React.ReactElement {
  return (
    <svg {...base}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  );
}

export function ApprovalsIcon(): React.ReactElement {
  return (
    <svg {...base}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export function HistoryIcon(): React.ReactElement {
  return (
    <svg {...base}>
      <path d="M3 3v5h5" />
      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function HabitsIcon(): React.ReactElement {
  // A friendly growing sprout — the kind "habits grow over time" motif.
  return (
    <svg {...base}>
      <path d="M12 22V11" />
      <path d="M12 11c0-3 2.2-5.2 5-5.2 0 3-2.2 5.2-5 5.2Z" />
      <path d="M12 13c0-2.6-1.9-4.6-4.5-4.6 0 2.6 1.9 4.6 4.5 4.6Z" />
    </svg>
  );
}

export function LearnIcon(): React.ReactElement {
  // An open book — the "learning games" motif (friendly, not school-stern).
  return (
    <svg {...base}>
      <path d="M12 7c-1.8-1.3-4-2-6.5-2H3v13h2.5c2.5 0 4.7.7 6.5 2 1.8-1.3 4-2 6.5-2H21V5h-2.5C16 5 13.8 5.7 12 7Z" />
      <path d="M12 7v13" />
    </svg>
  );
}

export function ConsoleIcon(): React.ReactElement {
  // Control sliders — the "Parent console" affordance (icon-only on narrow screens).
  return (
    <svg {...base}>
      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" />
      <path d="M1 14h6M9 8h6M17 16h6" />
    </svg>
  );
}

export function LogoutIcon(): React.ReactElement {
  // Exit-door arrow — the "Log out" button (icon-only on narrow screens).
  return (
    <svg {...base}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}
